import React, { useState } from 'react';
import {
  HomeIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ShareIcon,
  ClockIcon,
  ChartBarIcon,
  BookOpenIcon,
  CogIcon,
  QuestionMarkCircleIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  BellIcon,
  CubeIcon
} from '@heroicons/react/24/outline';

const DocumentationPage = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: RocketLaunchIcon,
      description: 'New to Sparvi? Start here'
    },
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: HomeIcon,
      description: 'Your data quality command center'
    },
    {
      id: 'data-explorer',
      title: 'Data Explorer',
      icon: MagnifyingGlassIcon,
      description: 'Browse and analyze your data'
    },
    {
      id: 'validations',
      title: 'Validations',
      icon: ShieldCheckIcon,
      description: 'Data quality rules and checks'
    },
    {
      id: 'issues',
      title: 'Issues',
      icon: ExclamationTriangleIcon,
      description: 'Track and resolve data problems'
    },
    {
      id: 'lineage',
      title: 'Lineage',
      icon: ShareIcon,
      description: 'Understand data relationships'
    },
    {
      id: 'automation',
      title: 'Automation',
      icon: ClockIcon,
      description: 'Automated processes and scheduling'
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: BellIcon,
      description: 'Email alerts and notification preferences'
    },
    {
      id: 'dbt-integration',
      title: 'dbt Integration',
      icon: CubeIcon,
      description: 'Connect and monitor dbt projects'
    },
    {
      id: 'analytics',
      title: 'Analytics',
      icon: ChartBarIcon,
      description: 'Data quality insights and trends'
    },
    {
      id: 'workflows',
      title: 'Common Workflows',
      icon: UserGroupIcon,
      description: 'Step-by-step guides'
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: QuestionMarkCircleIcon,
      description: 'Solutions to common problems'
    }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'getting-started':
        return <GettingStartedContent />;
      case 'dashboard':
        return <DashboardContent />;
      case 'data-explorer':
        return <DataExplorerContent />;
      case 'validations':
        return <ValidationsContent />;
      case 'issues':
        return <IssuesContent />;
      case 'lineage':
        return <LineageContent />;
      case 'automation':
        return <AutomationContent />;
      case 'notifications':
        return <NotificationsContent />;
      case 'dbt-integration':
        return <DbtIntegrationContent />;
      case 'analytics':
        return <AnalyticsContent />;
      case 'workflows':
        return <WorkflowsContent />;
      case 'troubleshooting':
        return <TroubleshootingContent />;
      default:
        return <GettingStartedContent />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <BookOpenIcon className="h-8 w-8 text-primary-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Sparvi Documentation</h1>
            </div>
            <div className="text-sm text-gray-500">
              Your guide to data quality excellence
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Navigation Sidebar */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <nav className="space-y-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        activeSection === section.id
                          ? 'bg-primary-50 text-primary-700 border-primary-200'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center">
                        <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                        <div>
                          <div className="font-medium">{section.title}</div>
                          <div className="text-xs text-gray-500 mt-1">{section.description}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Content Components
const GettingStartedContent = () => (
  <div className="prose max-w-none">
    <h2 className="text-3xl font-bold text-gray-900 mb-6">Welcome to Sparvi</h2>
    
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
      <h3 className="text-lg font-semibold text-blue-900 mb-2">What is Sparvi?</h3>
      <p className="text-blue-800">
        Sparvi is a comprehensive data quality and profiling engine designed for modern data warehouses. 
        It helps you monitor data pipelines, detect anomalies, track schema changes, and ensure data 
        integrity with precision and reliability.
      </p>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Concepts</h3>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Data Connections</h4>
        <p className="text-gray-600 text-sm">
          Connect Sparvi to your data warehouses (Snowflake, PostgreSQL) to begin monitoring 
          and profiling your tables.
        </p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Data Profiling</h4>
        <p className="text-gray-600 text-sm">
          Automated analysis of your data to understand quality metrics like completeness, 
          uniqueness, and distribution patterns.
        </p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Validation Rules</h4>
        <p className="text-gray-600 text-sm">
          Custom SQL-based rules that define what "good" data looks like for your specific 
          business requirements.
        </p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Data Lineage</h4>
        <p className="text-gray-600 text-sm">
          Visual representation of how data flows through your systems, helping you understand 
          dependencies and impact.
        </p>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">First Steps</h3>
    
    <ol className="list-decimal list-inside space-y-3 text-gray-700">
      <li><strong>Connect Your Data:</strong> Set up connections to your databases and select tables to monitor</li>
      <li><strong>Explore Your Data:</strong> Use the Data Explorer to understand your table structure and content</li>
      <li><strong>Create Validations:</strong> Define quality rules that matter to your business</li>
      <li><strong>Monitor Quality:</strong> Use the Dashboard to track data health over time</li>
      <li><strong>Manage Issues:</strong> Create and track data quality issues as they arise</li>
    </ol>

    <div className="bg-green-50 border border-green-200 rounded-lg p-6 mt-8">
      <h4 className="font-semibold text-green-900 mb-2">Pro Tip</h4>
      <p className="text-green-800">
        Start small with a few important tables and gradually expand your monitoring as you 
        become comfortable with Sparvi's features. Focus on business-critical data first.
      </p>
    </div>
  </div>
);

const DashboardContent = () => (
  <div className="prose max-w-none">
    <h2 className="text-3xl font-bold text-gray-900 mb-6">Dashboard</h2>
    
    <p className="text-lg text-gray-600 mb-8">
      Your data quality command center provides a comprehensive overview of data health across all your connections.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Overview Cards</h3>
    <p className="text-gray-700 mb-6">
      The top section displays key metrics at a glance:
    </p>
    
    <ul className="list-disc list-inside space-y-2 text-gray-700 mb-8">
      <li><strong>Total Tables:</strong> Number of tables being monitored across all connections</li>
      <li><strong>Data Quality Score:</strong> Overall health score based on validation results</li>
      <li><strong>Active Issues:</strong> Number of unresolved data quality problems</li>
      <li><strong>Recent Validations:</strong> Count of validation checks run in the last 24 hours</li>
    </ul>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Connection Health</h3>
    <p className="text-gray-700 mb-6">
      View the status and performance of each database connection, including:
    </p>
    
    <ul className="list-disc list-inside space-y-2 text-gray-700 mb-8">
      <li>Connection status and last successful sync</li>
      <li>Number of tables being monitored per connection</li>
      <li>Recent validation results and trends</li>
      <li>Quick access to connection settings</li>
    </ul>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h3>
    <p className="text-gray-700 mb-6">
      Stay informed about what's happening with your data:
    </p>
    
    <ul className="list-disc list-inside space-y-2 text-gray-700 mb-8">
      <li>New data quality issues discovered</li>
      <li>Validation rule failures and successes</li>
      <li>Schema changes detected</li>
      <li>Automated profiling completion</li>
    </ul>

    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
      <h4 className="font-semibold text-blue-900 mb-2">Using the Dashboard Effectively</h4>
      <ul className="list-disc list-inside space-y-1 text-blue-800">
        <li>Check the dashboard daily to stay on top of data quality trends</li>
        <li>Use the activity feed to quickly identify and respond to new issues</li>
        <li>Monitor connection health to ensure continuous data monitoring</li>
        <li>Click on any metric to drill down into detailed views</li>
      </ul>
    </div>
  </div>
);

const DataExplorerContent = () => (
  <div className="prose max-w-none">
    <h2 className="text-3xl font-bold text-gray-900 mb-6">Data Explorer</h2>
    
    <p className="text-lg text-gray-600 mb-8">
      Navigate and analyze your data structure with detailed insights into tables, columns, and data quality.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Browsing Your Data</h3>
    
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
      <h4 className="font-semibold text-gray-900 mb-3">Navigation Hierarchy</h4>
      <p className="text-gray-700">
        <strong>Connection → Database → Schema → Table</strong>
      </p>
      <p className="text-gray-600 text-sm mt-2">
        Use the left sidebar to navigate through your data structure, starting from connections down to individual tables.
      </p>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Table Views</h3>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Overview</h4>
        <p className="text-gray-600 text-sm">
          General table information including row counts, column counts, 
          and basic metadata about the table structure.
        </p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Preview</h4>
        <p className="text-gray-600 text-sm">
          Sample data from the table to understand content and structure. 
          Configure the number of rows to preview.
        </p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Columns</h4>
        <p className="text-gray-600 text-sm">
          Detailed column information including data types, constraints, 
          and column-level quality metrics.
        </p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Profile</h4>
        <p className="text-gray-600 text-sm">
          Statistical analysis of data quality including completeness, 
          uniqueness, and distribution patterns.
        </p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Validations</h4>
        <p className="text-gray-600 text-sm">
          Results of validation rules applied to this table, including 
          pass/fail status and historical trends.
        </p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Relationships</h4>
        <p className="text-gray-600 text-sm">
          Data lineage showing upstream and downstream dependencies 
          for this table.
        </p>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Quality Insights</h3>
    <p className="text-gray-700 mb-6">
      Each table view provides different quality insights:
    </p>
    
    <ul className="list-disc list-inside space-y-2 text-gray-700 mb-8">
      <li><strong>Completeness:</strong> Percentage of non-null values in each column</li>
      <li><strong>Uniqueness:</strong> Duplicate detection and cardinality analysis</li>
      <li><strong>Data Types:</strong> Consistency and validity of data types</li>
      <li><strong>Patterns:</strong> Common formats and pattern analysis</li>
      <li><strong>Outliers:</strong> Statistical outliers and anomalies</li>
    </ul>

    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
      <h4 className="font-semibold text-green-900 mb-2">Exploration Tips</h4>
      <ul className="list-disc list-inside space-y-1 text-green-800">
        <li>Start with the Overview tab to understand table basics</li>
        <li>Use Preview to familiarize yourself with actual data</li>
        <li>Check the Profile tab for detailed quality metrics</li>
        <li>Review Validations to see automated quality checks</li>
        <li>Explore Relationships to understand data dependencies</li>
      </ul>
    </div>
  </div>
);

const ValidationsContent = () => (
  <div className="prose max-w-none">
    <h2 className="text-3xl font-bold text-gray-900 mb-6">Validations</h2>
    
    <p className="text-lg text-gray-600 mb-8">
      Create and manage automated data quality rules that continuously monitor your data against business requirements.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">What are Validations?</h3>
    <p className="text-gray-700 mb-6">
      Validations are SQL-based rules that define what "good" data looks like for your organization. 
      They automatically check your data and alert you when quality issues arise.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Types of Validation Rules</h3>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Data Quality</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Completeness checks (non-null values)</li>
          <li>• Uniqueness validation</li>
          <li>• Range and boundary checks</li>
          <li>• Pattern matching</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Business Rules</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Referential integrity</li>
          <li>• Business logic validation</li>
          <li>• Cross-table consistency</li>
          <li>• Regulatory compliance</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Statistical</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Distribution analysis</li>
          <li>• Outlier detection</li>
          <li>• Trend validation</li>
          <li>• Volume monitoring</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Freshness</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Data recency checks</li>
          <li>• Update frequency validation</li>
          <li>• Lag detection</li>
          <li>• SLA monitoring</li>
        </ul>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Creating Validation Rules</h3>
    
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
      <h4 className="font-semibold text-gray-900 mb-3">Rule Components</h4>
      <ul className="space-y-2 text-gray-700">
        <li><strong>Rule Name:</strong> Unique identifier for the validation</li>
        <li><strong>Description:</strong> Clear explanation of what the rule checks</li>
        <li><strong>SQL Query:</strong> The logic that performs the validation</li>
        <li><strong>Expected Result:</strong> What the query should return for "good" data</li>
        <li><strong>Comparison Operator:</strong> How to compare actual vs expected results</li>
      </ul>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Example Validation Rules</h3>
    
    <div className="space-y-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Completeness Check</h4>
        <p className="text-gray-600 text-sm mb-2">Ensure email field is never null</p>
        <code className="block bg-gray-100 p-2 rounded text-sm">
          SELECT COUNT(*) FROM users WHERE email IS NULL
        </code>
        <p className="text-gray-600 text-xs mt-2">Expected: 0 (equals operator)</p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Range Validation</h4>
        <p className="text-gray-600 text-sm mb-2">Check that ages are reasonable</p>
        <code className="block bg-gray-100 p-2 rounded text-sm">
          SELECT COUNT(*) FROM users WHERE age &lt; 0 OR age &gt; 120
        </code>
        <p className="text-gray-600 text-xs mt-2">Expected: 0 (equals operator)</p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Business Rule</h4>
        <p className="text-gray-600 text-sm mb-2">Verify order total equals sum of line items</p>
        <code className="block bg-gray-100 p-2 rounded text-sm">
          SELECT COUNT(*) FROM orders o WHERE o.total != (SELECT SUM(quantity * price) FROM order_items WHERE order_id = o.id)
        </code>
        <p className="text-gray-600 text-xs mt-2">Expected: 0 (equals operator)</p>
      </div>
    </div>

    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
      <h4 className="font-semibold text-yellow-900 mb-2">Best Practices</h4>
      <ul className="list-disc list-inside space-y-1 text-yellow-800">
        <li>Start with simple, critical validations before adding complex rules</li>
        <li>Use descriptive names that clearly explain what the rule checks</li>
        <li>Test validation queries manually before adding them to Sparvi</li>
        <li>Consider performance impact on large tables</li>
        <li>Regularly review and update rules as business requirements change</li>
      </ul>
    </div>
  </div>
);

const IssuesContent = () => (
  <div className="prose max-w-none">
    <h2 className="text-3xl font-bold text-gray-900 mb-6">Issues</h2>
    
    <p className="text-lg text-gray-600 mb-8">
      Track, manage, and resolve data quality problems with a collaborative approach to maintaining data integrity.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">What are Data Quality Issues?</h3>
    <p className="text-gray-700 mb-6">
      Issues represent data quality problems that require attention, investigation, or resolution. 
      They can be automatically detected by validation rules or manually created by team members.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Issue Lifecycle</h3>
    
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
      <div className="flex items-center space-x-8">
        <div className="text-center">
          <div className="w-10 h-10 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-semibold mb-2">1</div>
          <div className="text-sm font-medium">Open</div>
          <div className="text-xs text-gray-500">Detected/Created</div>
        </div>
        <div className="flex-1 h-0.5 bg-gray-300"></div>
        <div className="text-center">
          <div className="w-10 h-10 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center font-semibold mb-2">2</div>
          <div className="text-sm font-medium">In Progress</div>
          <div className="text-xs text-gray-500">Being Investigated</div>
        </div>
        <div className="flex-1 h-0.5 bg-gray-300"></div>
        <div className="text-center">
          <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-semibold mb-2">3</div>
          <div className="text-sm font-medium">Resolved</div>
          <div className="text-xs text-gray-500">Fixed/Closed</div>
        </div>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Issue Types</h3>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Data Quality</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Missing or null data</li>
          <li>• Duplicate records</li>
          <li>• Invalid formats or patterns</li>
          <li>• Outliers and anomalies</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Schema Issues</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Unexpected schema changes</li>
          <li>• Missing columns or tables</li>
          <li>• Data type mismatches</li>
          <li>• Constraint violations</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Business Logic</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Rule violations</li>
          <li>• Cross-table inconsistencies</li>
          <li>• Referential integrity errors</li>
          <li>• Compliance violations</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Freshness</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Stale data</li>
          <li>• Missing updates</li>
          <li>• Delayed data loads</li>
          <li>• SLA breaches</li>
        </ul>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Managing Issues</h3>
    
    <div className="space-y-4 mb-8">
      <div className="border-l-4 border-blue-400 pl-4">
        <h4 className="font-semibold text-gray-900">Assignment</h4>
        <p className="text-gray-600 text-sm">Assign issues to team members responsible for investigation and resolution.</p>
      </div>
      
      <div className="border-l-4 border-green-400 pl-4">
        <h4 className="font-semibold text-gray-900">Priority Levels</h4>
        <p className="text-gray-600 text-sm">Set priority (Critical, High, Medium, Low) based on business impact.</p>
      </div>
      
      <div className="border-l-4 border-purple-400 pl-4">
        <h4 className="font-semibold text-gray-900">Documentation</h4>
        <p className="text-gray-600 text-sm">Add comments, context, and resolution notes for future reference.</p>
      </div>
      
      <div className="border-l-4 border-orange-400 pl-4">
        <h4 className="font-semibold text-gray-900">Impact Assessment</h4>
        <p className="text-gray-600 text-sm">Understand which downstream systems and stakeholders are affected.</p>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Resolution Process</h3>
    
    <ol className="list-decimal list-inside space-y-3 text-gray-700 mb-8">
      <li><strong>Investigation:</strong> Analyze the issue to understand root cause and scope</li>
      <li><strong>Impact Assessment:</strong> Determine which systems and users are affected</li>
      <li><strong>Solution Planning:</strong> Develop a plan to fix the underlying problem</li>
      <li><strong>Implementation:</strong> Execute the fix in appropriate environments</li>
      <li><strong>Verification:</strong> Confirm the issue is resolved and quality is restored</li>
      <li><strong>Documentation:</strong> Record the resolution for future reference</li>
    </ol>

    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
      <h4 className="font-semibold text-blue-900 mb-2">Issue Management Tips</h4>
      <ul className="list-disc list-inside space-y-1 text-blue-800">
        <li>Triage new issues promptly to assess business impact</li>
        <li>Use clear, descriptive titles that explain the problem</li>
        <li>Include relevant context like affected time periods or data volumes</li>
        <li>Link related issues to track patterns and systemic problems</li>
        <li>Regular review meetings help ensure issues don't fall through cracks</li>
      </ul>
    </div>
  </div>
);

const LineageContent = () => (
  <div className="prose max-w-none">
    <h2 className="text-3xl font-bold text-gray-900 mb-6">Lineage</h2>
    
    <p className="text-lg text-gray-600 mb-8">
      Understand data relationships and dependencies to make informed decisions about changes and troubleshoot data issues effectively.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">What is Data Lineage?</h3>
    <p className="text-gray-700 mb-6">
      Data lineage provides a visual map of how data flows through your systems, showing the relationships 
      between tables, views, and other data objects. It helps you understand data dependencies and assess 
      the impact of changes.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Lineage Components</h3>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Data Objects</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Tables and views</li>
          <li>• Materialized views</li>
          <li>• Stored procedures</li>
          <li>• External data sources</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Relationships</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Direct table dependencies</li>
          <li>• View definitions</li>
          <li>• JOIN relationships</li>
          <li>• ETL/ELT processes</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Metadata</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Object types and schemas</li>
          <li>• Business criticality</li>
          <li>• Update frequency</li>
          <li>• Data quality scores</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Stakeholders</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Data owners</li>
          <li>• Consumer teams</li>
          <li>• Business users</li>
          <li>• System dependencies</li>
        </ul>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Lineage Views</h3>
    
    <div className="space-y-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Object-Level Lineage</h4>
        <p className="text-gray-600 text-sm mb-2">
          Detailed view of a specific data object showing all upstream dependencies (data sources) 
          and downstream consumers (systems that use this data).
        </p>
        <p className="text-gray-500 text-xs">Access via: Data Explorer → Table → Relationships tab</p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Graph View</h4>
        <p className="text-gray-600 text-sm mb-2">
          Interactive network diagram showing multiple data objects and their relationships. 
          Useful for understanding complex data flows and identifying patterns.
        </p>
        <p className="text-gray-500 text-xs">Access via: Lineage page in main navigation</p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Impact Analysis</h4>
        <p className="text-gray-600 text-sm mb-2">
          Analyze the potential impact of changes to a data object by understanding 
          all downstream dependencies and affected stakeholders.
        </p>
        <p className="text-gray-500 text-xs">Access via: Issues page when creating or investigating issues</p>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Common Use Cases</h3>
    
    <div className="space-y-4 mb-8">
      <div className="border-l-4 border-blue-400 pl-4">
        <h4 className="font-semibold text-gray-900">Change Impact Assessment</h4>
        <p className="text-gray-600 text-sm">Before modifying a table or process, understand what systems depend on it.</p>
      </div>
      
      <div className="border-l-4 border-green-400 pl-4">
        <h4 className="font-semibold text-gray-900">Root Cause Analysis</h4>
        <p className="text-gray-600 text-sm">When data quality issues occur, trace upstream to find the source of the problem.</p>
      </div>
      
      <div className="border-l-4 border-purple-400 pl-4">
        <h4 className="font-semibold text-gray-900">Data Discovery</h4>
        <p className="text-gray-600 text-sm">Find related data sources and understand how different datasets connect.</p>
      </div>
      
      <div className="border-l-4 border-orange-400 pl-4">
        <h4 className="font-semibold text-gray-900">Compliance Reporting</h4>
        <p className="text-gray-600 text-sm">Document data flows for regulatory requirements and audit trails.</p>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Understanding Lineage Diagrams</h3>
    
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
      <h4 className="font-semibold text-gray-900 mb-3">Visual Elements</h4>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <strong>Boxes:</strong> Data objects (tables, views)
        </div>
        <div>
          <strong>→ Arrows:</strong> Data flow direction
        </div>
        <div>
          <strong>Red indicators:</strong> Quality issues
        </div>
        <div>
          <strong>Yellow indicators:</strong> Warnings
        </div>
        <div>
          <strong>Green indicators:</strong> Healthy status
        </div>
        <div>
          <strong>Line thickness:</strong> Relationship strength
        </div>
      </div>
    </div>

    <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
      <h4 className="font-semibold text-purple-900 mb-2">Lineage Analysis Tips</h4>
      <ul className="list-disc list-inside space-y-1 text-purple-800">
        <li>Start with critical business tables and trace their dependencies</li>
        <li>Use filters to focus on specific object types or criticality levels</li>
        <li>Check lineage before making schema changes or deprecating tables</li>
        <li>Document data ownership and responsibilities using stakeholder information</li>
        <li>Regular lineage reviews help maintain data architecture understanding</li>
      </ul>
    </div>
  </div>
);

const AutomationContent = () => (
  <div className="prose max-w-none">
    <h2 className="text-3xl font-bold text-gray-900 mb-6">Automation</h2>
    
    <p className="text-lg text-gray-600 mb-8">
      Set up automated processes to continuously monitor data quality without manual intervention.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">What is Automation?</h3>
    <p className="text-gray-700 mb-6">
      Automation in Sparvi allows you to schedule recurring data profiling, validation checks, 
      and quality monitoring tasks. This ensures continuous oversight of your data quality 
      without requiring manual execution.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Automation Types</h3>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Data Profiling</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Scheduled statistical analysis</li>
          <li>• Column profiling updates</li>
          <li>• Data distribution monitoring</li>
          <li>• Quality metric calculation</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Validation Execution</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Automatic rule execution</li>
          <li>• Quality check scheduling</li>
          <li>• Business rule validation</li>
          <li>• Compliance monitoring</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Anomaly Detection</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Statistical outlier detection</li>
          <li>• Pattern change monitoring</li>
          <li>• Volume anomaly alerts</li>
          <li>• Trend analysis</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Metadata Collection</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Schema change detection</li>
          <li>• Table metadata updates</li>
          <li>• Lineage discovery</li>
          <li>• Usage pattern tracking</li>
        </ul>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Scheduling Options</h3>
    
    <div className="space-y-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Time-Based Schedules</h4>
        <p className="text-gray-600 text-sm mb-2">
          Configure automation to run at specific times and intervals:
        </p>
        <ul className="text-gray-600 text-sm space-y-1 ml-4">
          <li>• Hourly, daily, weekly, or monthly intervals</li>
          <li>• Specific times of day or days of week</li>
          <li>• Custom cron expressions for complex schedules</li>
          <li>• Time zone considerations for global teams</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Event-Based Triggers</h4>
        <p className="text-gray-600 text-sm mb-2">
          Trigger automation based on specific events:
        </p>
        <ul className="text-gray-600 text-sm space-y-1 ml-4">
          <li>• Data load completion</li>
          <li>• Schema changes detected</li>
          <li>• Quality threshold breaches</li>
          <li>• Manual trigger requests</li>
        </ul>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Monitoring Automation</h3>
    
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
      <h4 className="font-semibold text-gray-900 mb-3">Automation Dashboard</h4>
      <p className="text-gray-700 mb-4">
        The automation page provides visibility into all scheduled and running processes:
      </p>
      <ul className="space-y-2 text-gray-700">
        <li><strong>Active Jobs:</strong> Currently running automation tasks</li>
        <li><strong>Upcoming Schedules:</strong> Next scheduled execution times</li>
        <li><strong>Recent History:</strong> Results of recent automation runs</li>
        <li><strong>Performance Metrics:</strong> Execution times and success rates</li>
      </ul>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Best Practices</h3>
    
    <div className="space-y-4 mb-8">
      <div className="border-l-4 border-blue-400 pl-4">
        <h4 className="font-semibold text-gray-900">Optimal Timing</h4>
        <p className="text-gray-600 text-sm">Schedule profiling after data loads complete but before business hours begin.</p>
      </div>
      
      <div className="border-l-4 border-green-400 pl-4">
        <h4 className="font-semibold text-gray-900">Resource Management</h4>
        <p className="text-gray-600 text-sm">Stagger automation schedules to avoid overloading your data warehouse.</p>
      </div>
      
      <div className="border-l-4 border-purple-400 pl-4">
        <h4 className="font-semibold text-gray-900">Failure Handling</h4>
        <p className="text-gray-600 text-sm">Configure retry policies and notifications for failed automation runs.</p>
      </div>
      
      <div className="border-l-4 border-orange-400 pl-4">
        <h4 className="font-semibold text-gray-900">Performance Monitoring</h4>
        <p className="text-gray-600 text-sm">Regularly review automation performance and adjust schedules as needed.</p>
      </div>
    </div>

    <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
      <h4 className="font-semibold text-orange-900 mb-2">Automation Setup Tips</h4>
      <ul className="list-disc list-inside space-y-1 text-orange-800">
        <li>Start with critical tables and gradually expand automation coverage</li>
        <li>Test automation schedules in development before production deployment</li>
        <li>Monitor resource usage and adjust frequency based on system capacity</li>
        <li>Set up alerts for automation failures to ensure continuous monitoring</li>
        <li>Document automation schedules and dependencies for team awareness</li>
      </ul>
    </div>
  </div>
);

const AnalyticsContent = () => (
  <div className="prose max-w-none">
    <h2 className="text-3xl font-bold text-gray-900 mb-6">Analytics</h2>
    
    <p className="text-lg text-gray-600 mb-8">
      Gain deep insights into data quality trends, patterns, and business impact through comprehensive analytics and reporting.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Analytics Overview</h3>
    <p className="text-gray-700 mb-6">
      Sparvi's analytics provide historical context and trend analysis for your data quality metrics, 
      helping you understand patterns, identify improvements, and demonstrate the business value of 
      data quality initiatives.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Analytics Types</h3>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Quality Trends</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Historical quality scores</li>
          <li>• Validation pass/fail rates</li>
          <li>• Data completeness trends</li>
          <li>• Issue resolution patterns</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Table Analytics</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Table-specific health metrics</li>
          <li>• Volume and growth patterns</li>
          <li>• Column-level quality analysis</li>
          <li>• Usage and access patterns</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Schema Changes</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Schema evolution tracking</li>
          <li>• Change frequency analysis</li>
          <li>• Impact assessment reports</li>
          <li>• Change approval workflows</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Business Impact</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Stakeholder impact analysis</li>
          <li>• Business process mapping</li>
          <li>• Cost of quality issues</li>
          <li>• SLA compliance tracking</li>
        </ul>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Metrics</h3>
    
    <div className="space-y-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Data Quality Score</h4>
        <p className="text-gray-600 text-sm mb-2">
          Composite score based on validation results, completeness, and consistency metrics across all monitored tables.
        </p>
        <p className="text-gray-500 text-xs">Range: 0-100, calculated daily</p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Validation Success Rate</h4>
        <p className="text-gray-600 text-sm mb-2">
          Percentage of validation rules that pass successfully over a given time period.
        </p>
        <p className="text-gray-500 text-xs">Calculated per table and organization-wide</p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Issue Resolution Time</h4>
        <p className="text-gray-600 text-sm mb-2">
          Average time from issue detection to resolution, helping measure team efficiency.
        </p>
        <p className="text-gray-500 text-xs">Tracked by priority level and issue type</p>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Data Coverage</h4>
        <p className="text-gray-600 text-sm mb-2">
          Percentage of your data that is actively monitored and validated by Sparvi.
        </p>
        <p className="text-gray-500 text-xs">Based on table count and data volume</p>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Reporting Features</h3>
    
    <div className="space-y-4 mb-8">
      <div className="border-l-4 border-blue-400 pl-4">
        <h4 className="font-semibold text-gray-900">Interactive Dashboards</h4>
        <p className="text-gray-600 text-sm">Drill down into specific time periods, tables, or quality dimensions.</p>
      </div>
      
      <div className="border-l-4 border-green-400 pl-4">
        <h4 className="font-semibold text-gray-900">Trend Analysis</h4>
        <p className="text-gray-600 text-sm">Identify patterns and seasonal variations in data quality metrics.</p>
      </div>
      
      <div className="border-l-4 border-purple-400 pl-4">
        <h4 className="font-semibold text-gray-900">Comparative Analysis</h4>
        <p className="text-gray-600 text-sm">Compare quality metrics across different tables, time periods, or environments.</p>
      </div>
      
      <div className="border-l-4 border-orange-400 pl-4">
        <h4 className="font-semibold text-gray-900">Export Capabilities</h4>
        <p className="text-gray-600 text-sm">Export reports and charts for presentations and external reporting needs.</p>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Using Analytics Effectively</h3>
    
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
      <h4 className="font-semibold text-gray-900 mb-3">Regular Review Practices</h4>
      <ul className="space-y-2 text-gray-700">
        <li><strong>Daily:</strong> Check overall quality scores and new issues</li>
        <li><strong>Weekly:</strong> Review validation trends and resolution progress</li>
        <li><strong>Monthly:</strong> Analyze long-term patterns and plan improvements</li>
        <li><strong>Quarterly:</strong> Business impact assessment and strategy review</li>
      </ul>
    </div>

    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
      <h4 className="font-semibold text-green-900 mb-2">Analytics Best Practices</h4>
      <ul className="list-disc list-inside space-y-1 text-green-800">
        <li>Establish baseline metrics before implementing major changes</li>
        <li>Focus on trends rather than absolute values for quality assessment</li>
        <li>Correlate quality metrics with business events and data pipeline changes</li>
        <li>Share analytics insights with stakeholders to demonstrate value</li>
        <li>Use analytics to prioritize data quality improvement initiatives</li>
      </ul>
    </div>
  </div>
);

const WorkflowsContent = () => (
  <div className="prose max-w-none">
    <h2 className="text-3xl font-bold text-gray-900 mb-6">Common Workflows</h2>
    
    <p className="text-lg text-gray-600 mb-8">
      Step-by-step guides for the most common tasks and workflows in Sparvi.
    </p>

    <div className="space-y-8">
      {/* Workflow 1: Setting up your first connection */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Setting Up Your First Connection</h3>
        
        <ol className="list-decimal list-inside space-y-3 text-gray-700">
          <li>
            <strong>Navigate to Connections:</strong> Click "Connections" in the sidebar or use the connection dropdown in the header
          </li>
          <li>
            <strong>Add New Connection:</strong> Click "Add New Connection" button
          </li>
          <li>
            <strong>Choose Database Type:</strong> Select Snowflake, PostgreSQL, or your database type
          </li>
          <li>
            <strong>Enter Connection Details:</strong>
            <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
              <li>Connection name (for identification)</li>
              <li>Host/Server information</li>
              <li>Database credentials</li>
              <li>Database and schema names</li>
            </ul>
          </li>
          <li>
            <strong>Test Connection:</strong> Click "Test Connection" to verify connectivity
          </li>
          <li>
            <strong>Save Connection:</strong> Once test passes, save your connection
          </li>
          <li>
            <strong>Select Tables:</strong> Choose which tables to monitor from the available list
          </li>
        </ol>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <p className="text-blue-800 text-sm">
            <strong>Tip:</strong> Start with 5-10 of your most critical tables to get familiar with Sparvi before expanding to your entire schema.
          </p>
        </div>
      </div>

      {/* Workflow 2: Creating validation rules */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Creating Your First Validation Rules</h3>
        
        <ol className="list-decimal list-inside space-y-3 text-gray-700">
          <li>
            <strong>Choose a Table:</strong> Navigate to Data Explorer → select a connection → choose a table
          </li>
          <li>
            <strong>Go to Validations Tab:</strong> Click the "Validations" tab in the table detail view
          </li>
          <li>
            <strong>Click "Add Validation":</strong> Start creating a new validation rule
          </li>
          <li>
            <strong>Define the Rule:</strong>
            <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
              <li>Rule name: Descriptive identifier (e.g., "check_email_completeness")</li>
              <li>Description: Clear explanation of what this rule checks</li>
              <li>SQL Query: Write a query that returns a metric to validate</li>
              <li>Expected Value: What the query should return for healthy data</li>
              <li>Comparison: Choose operator (equals, greater than, less than, etc.)</li>
            </ul>
          </li>
          <li>
            <strong>Test the Rule:</strong> Use "Test Query" to verify your SQL works correctly
          </li>
          <li>
            <strong>Save and Run:</strong> Save the rule and run it to see initial results
          </li>
        </ol>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
          <p className="text-yellow-800 text-sm">
            <strong>Best Practice:</strong> Start with simple rules like null checks and row counts before creating complex business logic validations.
          </p>
        </div>
      </div>

      {/* Workflow 3: Investigating data quality issues */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Investigating Data Quality Issues</h3>
        
        <ol className="list-decimal list-inside space-y-3 text-gray-700">
          <li>
            <strong>Identify the Issue:</strong> Check the Dashboard for alerts or browse the Issues page
          </li>
          <li>
            <strong>Review Issue Details:</strong> Click on the issue to see description, affected tables, and timeline
          </li>
          <li>
            <strong>Check Data Explorer:</strong> Navigate to the affected table to examine current data state
          </li>
          <li>
            <strong>Review Validation History:</strong> Look at validation trends to understand when the issue started
          </li>
          <li>
            <strong>Examine Lineage:</strong> Use the Relationships tab to understand upstream dependencies
          </li>
          <li>
            <strong>Gather Context:</strong>
            <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
              <li>Recent schema changes</li>
              <li>Data pipeline modifications</li>
              <li>Business process changes</li>
              <li>System maintenance or updates</li>
            </ul>
          </li>
          <li>
            <strong>Document Findings:</strong> Add comments to the issue with your investigation notes
          </li>
          <li>
            <strong>Assign and Track:</strong> Assign the issue to appropriate team member for resolution</li>
        </ol>
      </div>

      {/* Workflow 4: Setting up automation */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Setting Up Automated Monitoring</h3>
        
        <ol className="list-decimal list-inside space-y-3 text-gray-700">
          <li>
            <strong>Navigate to Automation:</strong> Click "Automation" in the sidebar
          </li>
          <li>
            <strong>Review Current Jobs:</strong> See what's already scheduled and running
          </li>
          <li>
            <strong>Create New Schedule:</strong> Click "Add Schedule" or "New Automation"
          </li>
          <li>
            <strong>Configure Schedule:</strong>
            <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
              <li>Choose automation type (profiling, validations, etc.)</li>
              <li>Select target tables or connections</li>
              <li>Set schedule frequency (hourly, daily, weekly)</li>
              <li>Configure time zone and specific timing</li>
            </ul>
          </li>
          <li>
            <strong>Set Notifications:</strong> Configure alerts for failures or important results
          </li>
          <li>
            <strong>Test Schedule:</strong> Run a test execution to verify configuration
          </li>
          <li>
            <strong>Monitor Performance:</strong> Check automation dashboard regularly for execution status
          </li>
        </ol>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
          <p className="text-green-800 text-sm">
            <strong>Recommendation:</strong> Schedule profiling and validations to run after your data loads complete but before business hours begin.
          </p>
        </div>
      </div>

      {/* Workflow 5: Team collaboration */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Setting Up Team Collaboration</h3>
        
        <ol className="list-decimal list-inside space-y-3 text-gray-700">
          <li>
            <strong>Define Data Ownership:</strong> Identify who owns each table and connection
          </li>
          <li>
            <strong>Create Validation Standards:</strong> Establish team guidelines for validation rules
          </li>
          <li>
            <strong>Set Up Issue Workflow:</strong>
            <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
              <li>Define priority levels and response times</li>
              <li>Establish assignment and escalation procedures</li>
              <li>Create templates for common issue types</li>
            </ul>
          </li>
          <li>
            <strong>Configure Notifications:</strong> Set up alerts for relevant team members
          </li>
          <li>
            <strong>Regular Review Meetings:</strong> Schedule weekly data quality reviews
          </li>
          <li>
            <strong>Documentation:</strong> Maintain shared documentation for validation rules and procedures
          </li>
        </ol>
      </div>
    </div>

    <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mt-8">
      <h4 className="font-semibold text-purple-900 mb-2">Workflow Success Tips</h4>
      <ul className="list-disc list-inside space-y-1 text-purple-800">
        <li>Start small and gradually expand your monitoring coverage</li>
        <li>Involve business stakeholders in defining validation rules</li>
        <li>Document your workflows and share knowledge across the team</li>
        <li>Regular review and improvement of your processes</li>
        <li>Leverage automation to reduce manual effort and ensure consistency</li>
      </ul>
    </div>
  </div>
);

const NotificationsContent = () => (
  <div className="prose max-w-none">
    <h2 className="text-3xl font-bold text-gray-900 mb-6">Notifications & Alerts</h2>

    <p className="text-lg text-gray-600 mb-8">
      Stay informed about data quality issues with customizable email notifications and alerts.
    </p>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Email Notification Options</h3>
    <p className="text-gray-700 mb-6">
      Sparvi gives you full control over when and how you receive notifications about data quality events.
      Configure your preferences to balance staying informed with managing email volume.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
        <h4 className="font-semibold text-gray-900 mb-2">Real-time Alerts</h4>
        <p className="text-gray-600 text-sm mb-3">
          Get notified immediately when issues are detected. Best for critical data pipelines and production systems.
        </p>
        <ul className="text-gray-600 text-xs space-y-1">
          <li>• Individual email per alert</li>
          <li>• Advanced filtering options</li>
          <li>• Severity level controls</li>
          <li>• Event type toggles</li>
        </ul>
      </div>

      <div className="border border-green-200 rounded-lg p-4 bg-green-50">
        <h4 className="font-semibold text-gray-900 mb-2">Weekly Digest (Recommended)</h4>
        <p className="text-gray-600 text-sm mb-3">
          Receive one summary email every Monday at 8:00 AM UTC. Reduces email noise by 95%+.
        </p>
        <ul className="text-gray-600 text-xs space-y-1">
          <li>• Weekly summary of all issues</li>
          <li>• Trend analysis (vs. last week)</li>
          <li>• Top 3 critical issues</li>
          <li>• Resolution statistics</li>
        </ul>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">No Email Notifications</h4>
        <p className="text-gray-600 text-sm mb-3">
          Turn off all email alerts. Issues still appear in your dashboard for review.
        </p>
        <ul className="text-gray-600 text-xs space-y-1">
          <li>• Zero email clutter</li>
          <li>• Dashboard-only notifications</li>
          <li>• Manual check required</li>
          <li>• Good for less critical systems</li>
        </ul>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Notification Types</h3>

    <div className="space-y-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Validation Failures</h4>
        <p className="text-gray-600 text-sm mb-2">
          Triggered when data quality rules fail. Includes validation rule name, table, severity level, and failure details.
        </p>
        <p className="text-gray-500 text-xs">Example: "Customer email validation failed - 25 records have invalid format"</p>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Anomaly Detection</h4>
        <p className="text-gray-600 text-sm mb-2">
          Machine learning detects unusual patterns in your data. Sudden spikes in NULL values, unexpected distributions, or volume changes.
        </p>
        <p className="text-gray-500 text-xs">Example: "NULL values in 'revenue' column jumped from 0.1% to 15%"</p>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Schema Changes</h4>
        <p className="text-gray-600 text-sm mb-2">
          Alerts when database structure changes. Tables/columns added, removed, or modified. Data type changes tracked.
        </p>
        <p className="text-gray-500 text-xs">Example: "Column 'customer_phone' removed from 'users' table"</p>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Automation Failures</h4>
        <p className="text-gray-600 text-sm mb-2">
          Notifies when scheduled jobs fail to run. Always high severity - automated processes should never fail silently.
        </p>
        <p className="text-gray-500 text-xs">Example: "Scheduled validation job failed to execute"</p>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Advanced Options (Real-time Alerts)</h3>

    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
      <h4 className="font-semibold text-gray-900 mb-3">Fine-tune Your Notifications</h4>
      <div className="space-y-3">
        <div>
          <strong className="text-gray-900">Minimum Severity:</strong>
          <p className="text-gray-600 text-sm">Choose which severity levels trigger emails (All issues / Medium and High / Only critical)</p>
        </div>
        <div>
          <strong className="text-gray-900">Event Type Toggles:</strong>
          <p className="text-gray-600 text-sm">Enable/disable notifications for specific event types (validation failures, anomalies, schema changes, automation failures)</p>
        </div>
        <div>
          <strong className="text-gray-900">Throttling:</strong>
          <p className="text-gray-600 text-sm">Automatic limit of 50 emails per day per notification type prevents spam</p>
        </div>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Configuring Notifications</h3>

    <ol className="list-decimal list-inside space-y-3 text-gray-700 mb-8">
      <li><strong>Navigate to Settings:</strong> Click your profile icon (top right) → Settings</li>
      <li><strong>Select Preferences Tab:</strong> Navigate to the Preferences section</li>
      <li><strong>Choose Email Frequency:</strong> Select Real-time, Weekly Digest, or Disabled</li>
      <li><strong>Configure Advanced Options:</strong> (Real-time only) Set severity and event type preferences</li>
      <li><strong>Save Changes:</strong> Click "Save Preferences" to apply your settings</li>
    </ol>

    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
      <h4 className="font-semibold text-blue-900 mb-2">Best Practices</h4>
      <ul className="list-disc list-inside space-y-1 text-blue-800">
        <li><strong>Production Systems:</strong> Use Real-time Alerts with Medium/High severity</li>
        <li><strong>Development/Staging:</strong> Use Weekly Digest to reduce noise</li>
        <li><strong>Analytics/Reporting:</strong> Weekly Digest with High severity only</li>
        <li><strong>Team Coordination:</strong> Ensure critical systems have at least one person on Real-time</li>
      </ul>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Troubleshooting Notifications</h3>

    <div className="space-y-4">
      <div className="border-l-4 border-red-400 pl-4">
        <h4 className="font-semibold text-gray-900">Not receiving emails</h4>
        <ul className="text-gray-600 text-sm space-y-1 mt-2">
          <li>• Check notification preferences are not set to "Disabled"</li>
          <li>• Verify your email address in profile settings</li>
          <li>• Check spam/junk folder</li>
          <li>• Ensure issues are being detected (check dashboard)</li>
          <li>• Verify user status is "active" in organization</li>
        </ul>
      </div>

      <div className="border-l-4 border-yellow-400 pl-4">
        <h4 className="font-semibold text-gray-900">Too many emails</h4>
        <ul className="text-gray-600 text-sm space-y-1 mt-2">
          <li>• Switch to Weekly Digest mode (reduces by 95%+)</li>
          <li>• Increase minimum severity to "High only"</li>
          <li>• Disable specific event types you don't need</li>
          <li>• Throttling automatically limits to 50/day per type</li>
        </ul>
      </div>
    </div>
  </div>
);

const DbtIntegrationContent = () => (
  <div className="prose max-w-none">
    <h2 className="text-3xl font-bold text-gray-900 mb-6">dbt Integration</h2>

    <p className="text-lg text-gray-600 mb-8">
      Connect your dbt Core projects to Sparvi for unified data quality monitoring and enhanced lineage tracking.
    </p>

    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
      <h3 className="text-lg font-semibold text-blue-900 mb-2">What is dbt?</h3>
      <p className="text-blue-800">
        dbt (data build tool) is a transformation tool that enables data teams to transform data in their warehouse
        using SQL select statements. Sparvi's integration syncs dbt metadata to provide enhanced lineage tracking
        and a unified view of your data transformations.
      </p>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Features</h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Lineage Integration</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Import dbt model dependencies</li>
          <li>• Track transformations between models</li>
          <li>• Visualize dbt DAG in Sparvi lineage</li>
          <li>• Understand data flow through dbt</li>
        </ul>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Metadata Sync</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Sync dbt models and sources</li>
          <li>• Import model descriptions and tags</li>
          <li>• Track column-level metadata</li>
          <li>• Preserve dbt documentation</li>
        </ul>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Table Mapping</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Link dbt models to database tables</li>
          <li>• Connect sources to Sparvi connections</li>
          <li>• Automatic relationship detection</li>
          <li>• Unified data catalog view</li>
        </ul>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Enhanced Monitoring</h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li>• Combine dbt metadata with Sparvi profiling</li>
          <li>• Single pane of glass for data quality</li>
          <li>• Track both transformations and data quality</li>
          <li>• Enrich lineage with transformation logic</li>
        </ul>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Setting Up dbt Integration</h3>

    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
      <h4 className="font-semibold text-gray-900 mb-3">Prerequisites</h4>
      <ul className="space-y-2 text-gray-700">
        <li><strong>dbt Project Files:</strong> Access to your dbt project directory (manifest.json, catalog.json)</li>
        <li><strong>Database Connection:</strong> Existing Sparvi connection to the data warehouse where dbt models are materialized</li>
        <li><strong>dbt Artifacts:</strong> Generated manifest and catalog files from running <code className="bg-gray-800 text-white px-2 py-1 rounded text-sm font-mono">dbt docs generate</code></li>
      </ul>
    </div>

    <ol className="list-decimal list-inside space-y-3 text-gray-700 mb-8">
      <li>
        <strong>Generate dbt Documentation:</strong> Run <code className="bg-gray-800 text-white px-2 py-1 rounded text-sm font-mono">dbt docs generate</code> in your dbt project
        <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
          <li>Creates manifest.json (model dependencies and metadata)</li>
          <li>Creates catalog.json (compiled model information)</li>
          <li>These files contain the lineage and metadata to sync</li>
        </ul>
      </li>
      <li>
        <strong>Upload dbt Artifacts to Sparvi:</strong> Use the dbt sync script or API endpoint
        <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
          <li>Backend sync script: <code className="bg-gray-800 text-white px-2 py-1 rounded text-sm font-mono">python scripts/sync_dbt_lineage.py</code></li>
          <li>Provide path to manifest.json and catalog.json files</li>
          <li>Specify the Sparvi connection ID for your data warehouse</li>
        </ul>
      </li>
      <li>
        <strong>Map dbt Sources:</strong> Link dbt source definitions to Sparvi connections
        <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
          <li>Identify which database connection each dbt source uses</li>
          <li>Ensures accurate lineage from source tables through dbt models</li>
        </ul>
      </li>
      <li>
        <strong>Verify Lineage:</strong> Check that dbt models appear in Sparvi's lineage view
        <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
          <li>Navigate to Lineage page in Sparvi</li>
          <li>Search for dbt model names</li>
          <li>Verify upstream/downstream relationships are correct</li>
        </ul>
      </li>
      <li>
        <strong>Schedule Regular Syncs:</strong> Keep dbt metadata up-to-date
        <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
          <li>Run sync script after dbt model changes</li>
          <li>Automate with CI/CD pipeline or cron job</li>
          <li>Recommended: sync after each dbt production run</li>
        </ul>
      </li>
    </ol>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Understanding dbt in Sparvi</h3>

    <div className="space-y-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Lineage Visualization</h4>
        <p className="text-gray-600 text-sm mb-2">
          Once synced, dbt models appear as data objects in Sparvi's lineage graph. You can visualize how source
          tables flow through dbt transformations to create final models.
        </p>
        <p className="text-gray-500 text-xs">Access via: Lineage page → Search for model names</p>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Model Relationships</h4>
        <p className="text-gray-600 text-sm mb-2">
          View upstream dependencies (sources and models this model depends on) and downstream consumers
          (models and tables that use this model's output).
        </p>
        <p className="text-gray-500 text-xs">Access via: Data Explorer → Table → Relationships tab</p>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Metadata Enrichment</h4>
        <p className="text-gray-600 text-sm mb-2">
          dbt descriptions, tags, and documentation are imported into Sparvi and displayed alongside
          Sparvi's data profiling and quality metrics.
        </p>
        <p className="text-gray-500 text-xs">Access via: Data Explorer → Table → Overview tab</p>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">dbt Sync Process</h3>

    <div className="space-y-4 mb-8">
      <div className="border-l-4 border-blue-400 pl-4">
        <h4 className="font-semibold text-gray-900">What Gets Synced</h4>
        <p className="text-gray-600 text-sm">Model names, dependencies (refs), source definitions, descriptions, tags, and column-level metadata from your dbt project.</p>
      </div>

      <div className="border-l-4 border-green-400 pl-4">
        <h4 className="font-semibold text-gray-900">Sync Frequency</h4>
        <p className="text-gray-600 text-sm">Run the sync script manually when needed, or automate it in your CI/CD pipeline to run after dbt deployments.</p>
      </div>

      <div className="border-l-4 border-purple-400 pl-4">
        <h4 className="font-semibold text-gray-900">Read-Only Integration</h4>
        <p className="text-gray-600 text-sm">Sparvi only reads dbt metadata - it does not modify your dbt project files or execute dbt commands.</p>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Best Practices</h3>

    <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
      <h4 className="font-semibold text-green-900 mb-2">dbt + Sparvi Workflow</h4>
      <ul className="list-disc list-inside space-y-1 text-green-800">
        <li>Sync dbt metadata after each production deployment to keep lineage current</li>
        <li>Use dbt descriptions to document transformation logic that appears in Sparvi</li>
        <li>Leverage both dbt's transformation lineage and Sparvi's data quality monitoring</li>
        <li>Add tags in dbt to categorize models in Sparvi's data catalog</li>
        <li>Use Sparvi validations to monitor dbt model outputs for data quality</li>
        <li>View complete lineage from raw sources through dbt transformations to final tables</li>
      </ul>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mb-4">Common Use Cases</h3>

    <div className="space-y-6">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Impact Analysis for Model Changes</h4>
        <ol className="text-gray-600 text-sm space-y-1 list-decimal list-inside">
          <li>Search for dbt model in Sparvi lineage</li>
          <li>View all downstream dependencies</li>
          <li>Identify which tables and reports will be affected</li>
          <li>Plan changes with full visibility of impact</li>
        </ol>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Understanding Data Provenance</h4>
        <ol className="text-gray-600 text-sm space-y-1 list-decimal list-inside">
          <li>Start with a final table or report in Sparvi</li>
          <li>Trace lineage backwards through dbt models</li>
          <li>Identify all source tables contributing to final output</li>
          <li>Understand full transformation path from source to destination</li>
        </ol>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Combined Quality Monitoring</h4>
        <ol className="text-gray-600 text-sm space-y-1 list-decimal list-inside">
          <li>Set up Sparvi validations on dbt model output tables</li>
          <li>Monitor both transformation success (dbt) and data quality (Sparvi)</li>
          <li>Use Sparvi's data explorer to investigate quality issues in dbt models</li>
          <li>Track quality metrics over time for transformed datasets</li>
        </ol>
      </div>
    </div>

    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-8">
      <h4 className="font-semibold text-yellow-900 mb-2">Note on dbt Core</h4>
      <p className="text-yellow-800 text-sm">
        Sparvi's current integration works with dbt Core projects via manifest and catalog files. The integration
        is read-only and focused on lineage and metadata enrichment. Run <code className="bg-gray-800 text-white px-2 py-1 rounded font-mono">dbt docs generate</code>
        to create the necessary artifact files for syncing.
      </p>
    </div>
  </div>
);

const TroubleshootingContent = () => (
  <div className="prose max-w-none">
    <h2 className="text-3xl font-bold text-gray-900 mb-6">Troubleshooting</h2>

    <p className="text-lg text-gray-600 mb-8">
      Solutions to common problems and guidance for resolving issues you might encounter while using Sparvi.
    </p>

    <div className="space-y-8">
      {/* Connection Issues */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Connection Issues</h3>
        
        <div className="space-y-4">
          <div className="border-l-4 border-red-400 pl-4">
            <h4 className="font-semibold text-gray-900">Can't connect to database</h4>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>• Verify host/server address and port are correct</li>
              <li>• Check username and password credentials</li>
              <li>• Ensure database and schema names exist</li>
              <li>• Confirm network connectivity and firewall settings</li>
              <li>• For Snowflake: verify account identifier format</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-yellow-400 pl-4">
            <h4 className="font-semibold text-gray-900">Connection times out</h4>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>• Check if database server is responding</li>
              <li>• Verify network latency and connectivity</li>
              <li>• Review database server load and performance</li>
              <li>• Consider increasing connection timeout settings</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-blue-400 pl-4">
            <h4 className="font-semibold text-gray-900">Permission denied errors</h4>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>• Verify user has SELECT permissions on target tables</li>
              <li>• Check schema and database access permissions</li>
              <li>• For Snowflake: ensure role has appropriate grants</li>
              <li>• Contact database administrator if needed</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Validation Issues */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Validation Rule Issues</h3>
        
        <div className="space-y-4">
          <div className="border-l-4 border-red-400 pl-4">
            <h4 className="font-semibold text-gray-900">Validation rule failing unexpectedly</h4>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>• Test the SQL query manually in your database</li>
              <li>• Check if table schema or data has changed</li>
              <li>• Verify expected values are still appropriate</li>
              <li>• Review validation history for patterns</li>
              <li>• Consider if business rules have evolved</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-yellow-400 pl-4">
            <h4 className="font-semibold text-gray-900">Validation query errors</h4>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>• Check SQL syntax and table/column names</li>
              <li>• Verify all referenced tables exist and are accessible</li>
              <li>• Ensure query returns a single numeric value</li>
              <li>• Test query with simplified version first</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-blue-400 pl-4">
            <h4 className="font-semibold text-gray-900">Slow validation execution</h4>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>• Review query performance and add indexes if needed</li>
              <li>• Consider sampling for large tables</li>
              <li>• Optimize joins and WHERE clauses</li>
              <li>• Schedule validations during off-peak hours</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Performance Issues */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Performance Issues</h3>
        
        <div className="space-y-4">
          <div className="border-l-4 border-red-400 pl-4">
            <h4 className="font-semibold text-gray-900">Slow data loading/profiling</h4>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>• Reduce number of tables processed simultaneously</li>
              <li>• Schedule profiling during low-usage periods</li>
              <li>• Consider sampling for very large tables</li>
              <li>• Check database server performance and load</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-yellow-400 pl-4">
            <h4 className="font-semibold text-gray-900">Browser performance issues</h4>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>• Clear browser cache and cookies</li>
              <li>• Disable unnecessary browser extensions</li>
              <li>• Use a modern, updated browser version</li>
              <li>• Reduce number of open browser tabs</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-blue-400 pl-4">
            <h4 className="font-semibold text-gray-900">Memory or resource issues</h4>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>• Monitor system resource usage during operations</li>
              <li>• Adjust automation schedules to spread load</li>
              <li>• Consider upgrading system resources if needed</li>
              <li>• Review large query results and implement pagination</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Data Issues */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Data Display Issues</h3>
        
        <div className="space-y-4">
          <div className="border-l-4 border-red-400 pl-4">
            <h4 className="font-semibold text-gray-900">Tables not showing up</h4>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>• Verify user has permissions to view the tables</li>
              <li>• Check if tables exist in the specified schema</li>
              <li>• Refresh the connection and reload the page</li>
              <li>• Ensure tables contain data (empty tables may be filtered)</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-yellow-400 pl-4">
            <h4 className="font-semibold text-gray-900">Incorrect data in previews</h4>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>• Check if you're viewing the correct table/schema</li>
              <li>• Verify connection is pointing to right database</li>
              <li>• Clear browser cache and refresh the page</li>
              <li>• Compare with direct database query to confirm</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-blue-400 pl-4">
            <h4 className="font-semibold text-gray-900">Missing column information</h4>
            <ul className="text-gray-600 text-sm space-y-1 mt-2">
              <li>• Refresh metadata by re-profiling the table</li>
              <li>• Check if recent schema changes occurred</li>
              <li>• Verify user has access to view column metadata</li>
              <li>• Re-run data profiling to update information</li>
            </ul>
          </div>
        </div>
      </div>

      {/* General Troubleshooting Tips */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">General Troubleshooting Tips</h3>
        
        <div className="space-y-3">
          <div className="bg-gray-50 border border-gray-200 rounded p-3">
            <h4 className="font-semibold text-gray-900 text-sm">Check browser console for errors</h4>
            <p className="text-gray-600 text-xs mt-1">Press F12 → Console tab to see detailed error messages</p>
          </div>
          
          <div className="bg-gray-50 border border-gray-200 rounded p-3">
            <h4 className="font-semibold text-gray-900 text-sm">Try incognito/private browsing mode</h4>
            <p className="text-gray-600 text-xs mt-1">Helps identify if browser extensions or cache are causing issues</p>
          </div>
          
          <div className="bg-gray-50 border border-gray-200 rounded p-3">
            <h4 className="font-semibold text-gray-900 text-sm">Test with minimal data set</h4>
            <p className="text-gray-600 text-xs mt-1">Use smaller tables or fewer validation rules to isolate problems</p>
          </div>
          
          <div className="bg-gray-50 border border-gray-200 rounded p-3">
            <h4 className="font-semibold text-gray-900 text-sm">Document the problem context</h4>
            <p className="text-gray-600 text-xs mt-1">Note what you were doing when the issue occurred and any error messages</p>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-red-50 border border-red-200 rounded-lg p-6 mt-8">
      <h4 className="font-semibold text-red-900 mb-2">When to Seek Help</h4>
      <p className="text-red-800 mb-3">Contact your system administrator or Sparvi support if:</p>
      <ul className="list-disc list-inside space-y-1 text-red-800 text-sm">
        <li>Issues persist after trying troubleshooting steps</li>
        <li>You suspect database connectivity or permission problems</li>
        <li>Performance issues affect multiple users or critical operations</li>
        <li>Data integrity concerns arise that could impact business decisions</li>
        <li>You need help setting up complex validation rules or automations</li>
      </ul>
    </div>
  </div>
);

export default DocumentationPage;